import { Avatar, Box, Text } from "@dehub/theme";
import type { DataTableColumn } from "@dehub/theme";
import { PencilSimpleIcon, UsersIcon } from "@phosphor-icons/react";
import type { TFunction } from "i18next";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import bz from "@/features/business/business.module.css";
import type { IWarehouseListRow } from "@/models/Warehouse";
import { RowAction } from "@/shared/a11y/row-action";
import { MissingPermission } from "@/shared/a11y/trans-code";
import { formatDate } from "@/utils/format-date";
import { renderName } from "@/utils/render-name";
import type { WarehousePermissions } from "../hooks/useWarehousePermissions";
import classes from "../warehouse.module.css";

type Args = {
  perms: WarehousePermissions;
  t: TFunction;
  tCommon: TFunction;
  onEdit: (row: IWarehouseListRow) => void;
  onStaff: (row: IWarehouseListRow) => void;
};

export const isWarehouseActive = (row: IWarehouseListRow) => row.warehouseStatus === "ACTIVE";

export function createWarehouseColumns({ perms, t, tCommon, onEdit, onStaff }: Args): DataTableColumn<IWarehouseListRow>[] {
  const missing = (...codes: string[]): ReactNode => <MissingPermission code={codes.join(", ")} />;

  return [
    {
      key: "actions",
      header: tCommon("col.action"),
      width: 92,
      render: (row) => {
        // Edit is the detail page in edit mode: it loads GET (PRT_WRH_DETAIL) and saves with PUT (PRT_WRH_EDIT).
        const editMissing: string[] = [];
        if (!perms.edit.hasAccess) editMissing.push(perms.codes.edit);
        if (!perms.detail.hasAccess) editMissing.push(perms.codes.detail);
        return (
          <Box className={bz.rowActions}>
            <RowAction
              label={tCommon("actions.edit")}
              reason={editMissing.length > 0 ? missing(...editMissing) : null}
              icon={PencilSimpleIcon}
              anchor={`${row.id}:edit`}
              onClick={() => onEdit(row)}
            />
            <RowAction
              label={t("list.staffAction")}
              reason={perms.detail.hasAccess ? null : missing(perms.codes.detail)}
              icon={UsersIcon}
              anchor={`${row.id}:staff`}
              onClick={() => onStaff(row)}
            />
          </Box>
        );
      },
    },
    {
      key: "warehouse",
      header: t("list.colWarehouse"),
      render: (row) => (
        <Box className={bz.bizText}>
          {perms.detail.hasAccess ? (
            <Text component={Link} to={`/our-warehouse/${row.id}`} className={`${bz.bizName} ${classes.nameLink}`}>
              {row.name || "-"}
            </Text>
          ) : (
            <Text component="span" className={bz.bizName}>
              {row.name || "-"}
            </Text>
          )}
          <Text component="span" className={bz.mono} translate="no">
            {row.refCode ?? "-"}
            {row.isDefault ? (
              <>
                {" · "}
                <Text component="span" inherit ff="var(--mantine-font-family)" fw={600}>
                  {t("list.main")}
                </Text>
              </>
            ) : null}
          </Text>
        </Box>
      ),
    },
    {
      key: "keeper",
      header: t("list.colKeeper"),
      render: (row) =>
        row.warehouseUser ? (
          <Box className={classes.keeper}>
            <Avatar src={row.warehouseUser.avatar || undefined} size={26} radius="xl" />
            <Text component="span" fz={14}>
              {renderName(row.warehouseUser)}
            </Text>
          </Box>
        ) : (
          <Text component={Link} to={`/our-warehouse/${row.id}?tab=staff`} className={classes.keeperLink}>
            {t("list.noKeeper")}
          </Text>
        ),
    },
    {
      key: "phone",
      header: tCommon("col.phone"),
      width: 130,
      render: (row) => (
        <Text component="span" className={bz.mono}>
          {row.phone || "-"}
        </Text>
      ),
    },
    {
      key: "goods",
      header: t("list.colGoods"),
      render: (row) =>
        row.isBuyer || row.isSupplier ? (
          <Box className={classes.chips}>
            {row.isBuyer ? <Box component="span" className={classes.chip}>{t("list.receives")}</Box> : null}
            {row.isSupplier ? <Box component="span" className={classes.chip}>{t("list.ships")}</Box> : null}
          </Box>
        ) : (
          <Text component="span" className={bz.muted}>
            -
          </Text>
        ),
    },
    {
      key: "status",
      header: tCommon("col.status"),
      width: 140,
      render: (row) => (
        <Box component="span" className={`${bz.pill} ${isWarehouseActive(row) ? bz.pillActive : ""}`}>
          <Box component="span" className={bz.pillDot} />
          {tCommon(`status.${isWarehouseActive(row) ? "ACTIVE" : "INACTIVE"}`)}
        </Box>
      ),
    },
    {
      key: "createdAt",
      header: t("list.colCreated"),
      width: 130,
      render: (row) => (
        <Text component="span" className={bz.mono}>
          {formatDate(row.createdAt) || "-"}
        </Text>
      ),
    },
  ];
}
